import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapLink from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Cropper from 'react-easy-crop';
import { 
  Menu, X, Shield, BookOpen, MapPin, TrendingDown, 
  Facebook, Instagram, Twitter, Calendar, Newspaper, ChevronRight,
  Mail, Phone, MapPinned, ExternalLink, Play, Quote, Award, User,
  Send, CheckCircle, Users, Download, Camera, ChevronLeft, Image, Building2,
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, AlignLeft, AlignCenter, Link2,
  Crop, RotateCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to resolve image URLs (handles both uploaded and external images)
const resolveImageUrl = (url) => {
  if (!url) return null;
  
  // Handle full URLs from old uploads - extract just the path
  if (url.includes('preview.emergentagent.com') || url.includes('emergent.host')) {
    const match = url.match(/\/api\/uploads\/[^?#]+/);
    if (match) {
      return `${BACKEND_URL}${match[0]}`;
    }
  }
  
  // Handle /api/uploads/ paths (new format)
  if (url.startsWith('/api/uploads')) {
    return `${BACKEND_URL}${url}`;
  }
  // Handle /uploads/ paths (old format) - convert to /api/uploads/
  if (url.startsWith('/uploads')) {
    return `${BACKEND_URL}/api${url}`;
  }
  return url;
};

// Icon mapping for dynamic icons from database
const ICON_MAP = {
  Shield: Shield,
  BookOpen: BookOpen,
  MapPin: MapPin,
  TrendingDown: TrendingDown,
  Award: Award,
  User: User,
  Users: Users,
  Calendar: Calendar,
  Building2: Building2
};

// Rich Text Editor Component using TipTap
const RichTextEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapLink.configure({
        openOnClick: false,
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-gray-300 rounded overflow-hidden bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-1 p-2 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
          title="Bold"
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
          title="Italic"
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
          title="Underline"
        >
          <UnderlineIcon size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={`p-2 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''}`}
          title="Heading"
        >
          H2
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={`p-2 rounded hover:bg-gray-200 text-sm font-bold ${editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''}`}
          title="Subheading"
        >
          H3
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
          title="Bullet List"
        >
          <List size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
          title="Numbered List"
        >
          <ListOrdered size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}`}
          title="Align Left"
        >
          <AlignLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}`}
          title="Align Center"
        >
          <AlignCenter size={18} />
        </button>
        
        <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
        
        <button
          type="button"
          onClick={setLink}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-200' : ''}`}
          title="Add Link"
        >
          <Link2 size={18} />
        </button>
      </div>
      
      {/* Editor */}
      <EditorContent editor={editor} className="prose max-w-none p-4 min-h-[200px] focus:outline-none" />
    </div>
  );
};

// Helper to create cropped image
const createCroppedImage = async (imageSrc, pixelCrop) => {
  const image = new window.Image();
  image.src = imageSrc;
  await new Promise((resolve) => { image.onload = resolve; });

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(blob);
    }, 'image/jpeg', 0.9);
  });
};

// Image Cropper Component
const ImageCropperModal = ({ imageSrc, onCropComplete, onCancel, aspectRatio = 1 }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [selectedAspect, setSelectedAspect] = useState(aspectRatio);

  const onCropChange = useCallback((crop) => {
    setCrop(crop);
  }, []);

  const onZoomChange = useCallback((zoom) => {
    setZoom(zoom);
  }, []);

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (croppedAreaPixels) {
      const croppedBlob = await createCroppedImage(imageSrc, croppedAreaPixels);
      onCropComplete(croppedBlob);
    }
  };

  const aspectOptions = [
    { label: 'Square (1:1)', value: 1 },
    { label: 'Landscape (4:3)', value: 4/3 },
    { label: 'Portrait (3:4)', value: 3/4 },
    { label: 'Wide (16:9)', value: 16/9 },
    { label: 'Free', value: null },
  ];

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-black">
        <div className="flex items-center gap-4">
          <Crop className="text-white" size={24} />
          <span className="text-white font-heading text-lg uppercase">Crop Image</span>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={selectedAspect || 'free'}
            onChange={(e) => setSelectedAspect(e.target.value === 'free' ? null : parseFloat(e.target.value))}
            className="bg-gray-800 text-white px-3 py-2 rounded border border-gray-600"
          >
            {aspectOptions.map((opt) => (
              <option key={opt.label} value={opt.value || 'free'}>{opt.label}</option>
            ))}
          </select>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>
        </div>
      </div>

      {/* Cropper */}
      <div className="flex-1 relative">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={selectedAspect}
          onCropChange={onCropChange}
          onCropComplete={onCropCompleteCallback}
          onZoomChange={onZoomChange}
        />
      </div>

      {/* Controls */}
      <div className="bg-black px-6 py-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div className="flex items-center gap-4 flex-1">
            <span className="text-white text-sm">Zoom:</span>
            <input
              type="range"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="flex-1 max-w-xs"
            />
            <span className="text-white text-sm w-12">{Math.round(zoom * 100)}%</span>
          </div>
          <div className="flex gap-4">
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-gray-700 text-white rounded hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-campaign-green text-white rounded hover:bg-campaign-green-hover transition-colors font-semibold"
            >
              Apply Crop
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Page title helper
const usePageTitle = (title) => {
  useEffect(() => {
    const baseTitle = "Bebe Ahmed - Candidate for Westchester County Legislator";
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;
  }, [title]);
};

// Campaign data
const CAMPAIGN_DATA = {
  donateUrl: "https://secure.winred.com/bebe-for-westchester/donate-today",
  facebook: "https://www.facebook.com/bebeforwestchester",
  instagram: "https://www.instagram.com/bebeahmedny",
  twitter: null,
  youtube: null,
  logo: "/bebe-logo-white.png",
  logoWhite: "/bebe-logo-white.png",
  logoRed: "/bebe-logo-dark.png",
  images: {
    heroMain: "/bebe-hero-new.jpg",
    heroMobile: "/bebe-headshot.jpg",
    bebeHeadshot: "/bebe-hero-new.jpg",
    campaignTrail: "/bebe-photo-main.jpg",
    meetingVoters: "/bebe-headshot.jpg",
  },
  gallery: [
    {
      url: "/bebe-photo-main.jpg",
      caption: "Bebe Ahmed on the campaign trail"
    },
    {
      url: "/bebe-headshot.jpg",
      caption: "Bebe Ahmed - Candidate for Westchester County Legislator"
    },
  ],
  tagline: "Retired Educator. Taxpaying Homeowner. District 14 Resident.",
  slogan: "Real Representation. Real Accountability.",
  subSlogan: "Voters deserve someone who can feel the impact of the legislation being passed.",
  endSlogan: "It's Time for Real Change.",
  bio: `Bebe Ahmed is a proud mother of two, a retired New York City public school teacher, and a longtime Westchester homeowner running for Westchester County Legislator, District 14.

She immigrated to the United States on July 4, 1976, and grew up in the South Bronx believing in the American Dream of hard work, education, and opportunity.`,
  bioShort: `Bebe has lived in Westchester for over 26 years, raising her family and paying property taxes for decades. She understands what these policies mean for working families.`,
  bioExtended: `She is running to bring accountability, affordability, and transparency back to county government and to ensure it works for the people of District 14. From Mount Vernon to McLean Avenue, Bebe will represent every neighborhood with respect, honesty, and common sense.`,
  contact: {
    website: "bebeforwestchester.com",
    email: "contact@bebeforwestchester.com",
    address: "Westchester County, NY",
    paidFor: "Bebe For Westchester"
  },
  donateAmounts: [5, 25, 50, 100],
  youtubeVideo: null,
  youtubeThumb: null,
  upcomingEvent: null,
  issues: [
    { 
      title: "Affordability & Tax Relief", 
      icon: TrendingDown, 
      desc: "Westchester families are paying more and getting less. Property taxes are unsustainable, energy bills are exploding, and the cost of living is pushing seniors and working families out.",
      details: [
        "Fight for responsible budgeting",
        "Smarter use of tax dollars",
        "Keep families in their communities",
        "Sustainable property taxes"
      ]
    },
    { 
      title: "Public Safety", 
      icon: Shield, 
      desc: "Public safety must come first. Residents deserve transparency, consequences for misconduct, and leadership that puts community safety ahead of politics.",
      details: [
        "Support law enforcement",
        "Demand accountability",
        "Protect law-abiding residents",
        "Community-first policing"
      ]
    },
    { 
      title: "Cost of Living", 
      icon: MapPin, 
      desc: "Unchecked energy costs are crushing our community. Rising Con Edison bills and costly state mandates are driving up expenses for homeowners, renters, and small businesses.",
      details: [
        "Stand up to unfair rate hikes",
        "Demand transparency from utilities",
        "Push back against costly mandates",
        "Protect working families"
      ]
    },
    { 
      title: "Real Representation", 
      icon: Users, 
      desc: "District 14 deserves a voice that understands the impact of county decisions. As a homeowner and mother, I know what these policies mean for families.",
      details: [
        "Represent every neighborhood",
        "Mount Vernon to McLean Avenue",
        "Honest, common-sense leadership",
        "Make government work for the people"
      ]
    },
  ]
};

// Navbar Component
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav data-testid="navbar" className={`${scrolled ? 'fixed top-0 left-0 right-0 shadow-lg' : 'relative'} z-50 transition-all duration-300`} style={{ background: 'linear-gradient(135deg, rgba(0, 61, 32, 0.9) 0%, rgba(0, 112, 54, 0.85) 100%)', backdropFilter: 'blur(8px)' }}>
      <div className="container-campaign">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src={CAMPAIGN_DATA.logoWhite} 
              alt="Bebe for Westchester" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-10">
            <Link to="/" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-home">Home</Link>
            <Link to="/about" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-about">About</Link>
            <Link to="/events" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-events">Events</Link>
            <Link to="/news" className="text-white hover:text-gray-300 text-lg font-black transition-colors duration-200" data-testid="nav-news">News</Link>
            <a 
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-donate text-lg"
              data-testid="nav-donate-btn"
            >
              Donate
            </a>
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="md:hidden p-2 text-white"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden mobile-menu-enter pb-4" data-testid="mobile-menu">
            <div className="flex flex-col space-y-4">
              <Link to="/" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Home</Link>
              <Link to="/about" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>About</Link>
              <Link to="/events" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>Events</Link>
              <Link to="/news" className="text-white hover:text-gray-300 py-2 font-black" onClick={() => setIsOpen(false)}>News</Link>
              <a 
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-donate text-center"
              >
                Donate
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="text-white py-16" style={{ background: 'linear-gradient(135deg, #003D20 0%, #007036 100%)' }}>
      <div className="container-campaign">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Tagline */}
          <div className="col-span-1 md:col-span-2">
            <img 
              src={CAMPAIGN_DATA.logoWhite} 
              alt="Bebe for Westchester" 
              className="h-20 w-auto mb-6"
            />
            <p className="text-gray-300 mb-2 font-black">
              {CAMPAIGN_DATA.slogan}
            </p>
            <p className="text-gray-400 mb-4 font-body">
              {CAMPAIGN_DATA.tagline}
            </p>
            <div className="flex space-x-4">
              {CAMPAIGN_DATA.twitter && (
                <a href={CAMPAIGN_DATA.twitter} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-twitter">
                  <Twitter size={20} />
                </a>
              )}
              {CAMPAIGN_DATA.facebook && (
                <a href={CAMPAIGN_DATA.facebook} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-facebook">
                  <Facebook size={20} />
                </a>
              )}
              {CAMPAIGN_DATA.instagram && (
                <a href={CAMPAIGN_DATA.instagram} target="_blank" rel="noopener noreferrer" className="social-icon" data-testid="footer-instagram">
                  <Instagram size={20} />
                </a>
              )}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/" className="text-gray-300 hover:text-white transition-colors font-semibold">Home</Link></li>
              <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors font-semibold">About Bebe</Link></li>
              <li><Link to="/events" className="text-gray-300 hover:text-white transition-colors font-semibold">Events</Link></li>
              <li><Link to="/news" className="text-gray-300 hover:text-white transition-colors font-semibold">In The News</Link></li>
            </ul>
          </div>
          
          {/* Legal */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><Link to="/terms-and-conditions" className="text-gray-300 hover:text-white transition-colors font-semibold">Terms & Conditions</Link></li>
              <li><Link to="/privacy-policy" className="text-gray-300 hover:text-white transition-colors font-semibold">Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-xl uppercase mb-4">Contact</h4>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-center gap-2">
                <Mail size={16} className="text-campaign-green" />
                <a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="hover:text-white transition-colors">
                  {CAMPAIGN_DATA.contact.email}
                </a>
              </li>
              <li className="flex items-center gap-2">
                <MapPinned size={16} className="text-campaign-green" />
                <span>{CAMPAIGN_DATA.contact.address}</span>
              </li>
              <li className="flex items-center gap-2">
                <ExternalLink size={16} className="text-campaign-green" />
                <a href={`https://${CAMPAIGN_DATA.contact.website}`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  {CAMPAIGN_DATA.contact.website}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/20 mt-12 pt-8 text-center text-gray-400 text-sm">
          <p className="font-semibold">Paid for by {CAMPAIGN_DATA.contact.paidFor}</p>
          <p className="mt-2">&copy; 2026 Bebe Ahmed</p>
        </div>
      </div>
    </footer>
  );
};

// Hero Section - Matching Anthony Merante style
const HeroSection = () => {
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    try {
      await axios.post(`${API}/subscribe`, { email });
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus(''), 3000);
    } catch (error) {
      setSubscribeStatus('error');
      setTimeout(() => setSubscribeStatus(''), 3000);
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-[500px] lg:h-[70vh] lg:min-h-[450px] lg:max-h-[650px] overflow-hidden">
      {/* Blurred Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(/bg-blurred.jpg)` }}
      />
      {/* Subtle dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/30" />
      
      {/* Content Container */}
      <div className="relative z-10 h-full py-8 lg:py-0">
        <div className="container-campaign w-full h-full">
          {/* Mobile Layout - Stacked */}
          <div className="flex flex-col items-center text-center lg:hidden">
            {/* Candidate Photo - Mobile */}
            <div className="w-1/2 max-w-[200px] mb-6 bg-white rounded-sm shadow-lg overflow-hidden">
              <img 
                src="/bebe-headshot-cutout.png"
                alt="Bebe Ahmed"
                className="w-full h-auto object-contain"
              />
            </div>
            
            {/* Campaign Logo - Mobile */}
            <img 
              src={CAMPAIGN_DATA.logoWhite}
              alt="Bebe for Westchester"
              className="h-24 w-auto mb-4"
            />
            
            {/* Slogan - Mobile */}
            <h1 className="font-heading text-2xl sm:text-3xl text-white uppercase mb-4 leading-tight">
              Real Representation.<br />Real Accountability.
            </h1>
            
            {/* Email Signup - Mobile */}
            <form onSubmit={handleSubscribe} className="flex flex-col gap-3 mb-4 w-full max-w-sm px-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-sm text-black placeholder-gray-500 focus:outline-none"
                data-testid="hero-email-input-mobile"
              />
              <button
                type="submit"
                className="bg-campaign-green-light hover:bg-campaign-green text-white font-heading uppercase px-6 py-3 transition-colors"
              >
                Stay Updated
              </button>
            </form>
            {subscribeStatus === 'success' && (
              <p className="text-green-300 text-sm mb-2">Thank you for subscribing!</p>
            )}
            {subscribeStatus === 'error' && (
              <p className="text-red-300 text-sm mb-2">Something went wrong. Please try again.</p>
            )}
            
            {/* CTA Buttons - Mobile */}
            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm px-4">
              <a 
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-1 bg-campaign-green-light text-white px-6 py-3 text-base font-heading uppercase tracking-wide hover:bg-white hover:text-campaign-green transition-colors text-center"
                data-testid="hero-donate-btn-mobile"
              >
                Donate Now
              </a>
              <Link 
                to="/about"
                className="flex-1 border-2 border-white text-white px-6 py-3 text-base font-heading uppercase tracking-wide hover:bg-white hover:text-campaign-green transition-colors text-center"
              >
                Get Involved
              </Link>
            </div>
          </div>
          
          {/* Desktop Layout - Side by Side */}
          <div className="hidden lg:grid grid-cols-2 gap-8 items-center h-full">
            {/* Left Side - Logo and Message */}
            <div className="text-left py-8">
              {/* Campaign Logo */}
              <img 
                src={CAMPAIGN_DATA.logoWhite}
                alt="Bebe for Westchester"
                className="h-28 md:h-36 lg:h-44 w-auto mb-6"
              />
              
              {/* Slogan */}
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white uppercase mb-6 leading-tight">
                Real Representation.<br />Real Accountability.
              </h1>
              
              {/* Email Signup */}
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 mb-6 max-w-md">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-sm text-black placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white"
                  data-testid="hero-email-input"
                />
                <button
                  type="submit"
                  className="bg-campaign-green-light hover:bg-campaign-green text-white font-heading uppercase px-6 py-3 transition-colors"
                  data-testid="hero-subscribe-btn"
                >
                  Stay Updated
                </button>
              </form>
              {subscribeStatus === 'success' && (
                <p className="text-green-300 text-sm mb-4">Thank you for subscribing!</p>
              )}
              {subscribeStatus === 'error' && (
                <p className="text-red-300 text-sm mb-4">Something went wrong. Please try again.</p>
              )}
              
              {/* CTA Buttons */}
              <div className="flex flex-row gap-4">
                <a 
                  href={CAMPAIGN_DATA.donateUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block bg-campaign-green-light text-white px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-white hover:text-campaign-green transition-colors text-center"
                  data-testid="hero-donate-btn"
                >
                  Donate Now
                </a>
                <Link 
                  to="/about"
                  className="inline-block border-2 border-white text-white px-8 py-4 text-lg font-heading uppercase tracking-wide hover:bg-white hover:text-campaign-green transition-colors text-center"
                  data-testid="hero-involved-btn"
                >
                  Get Involved
                </Link>
              </div>
            </div>
            
            {/* Right Side - Candidate Photo - Aligned to bottom */}
            <div className="flex items-end justify-end h-full">
              <img 
                src="/bebe-headshot-cutout.png"
                alt="Bebe Ahmed - Candidate for Westchester County Legislator"
                className="max-h-full w-auto object-contain object-bottom"
                data-testid="hero-candidate-photo"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Meet Bebe Section
const MeetBrandonSection = () => {
  return (
    <section data-testid="meet-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="flex flex-col items-center text-center">
          {/* Centered Photo */}
          <div className="w-full md:w-[70%] mb-10">
            <img 
              src={CAMPAIGN_DATA.images.bebeHeadshot}
              alt="Bebe Ahmed - Candidate for Westchester County Legislator" 
              className="w-full h-auto rounded-sm shadow-2xl"
              data-testid="meet-image"
            />
          </div>
          
          {/* Text Content Below */}
          <div className="max-w-3xl">
            <p className="text-campaign-green font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.tagline}</p>
            <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-6" data-testid="meet-title">
              Meet Bebe Ahmed
            </h2>
            <p className="text-lg text-gray-700 font-body leading-relaxed mb-6">
              {CAMPAIGN_DATA.bio}
            </p>
            <p className="text-gray-600 font-body leading-relaxed mb-6">
              {CAMPAIGN_DATA.bioShort}
            </p>
            <p className="text-gray-600 font-body leading-relaxed mb-8">
              {CAMPAIGN_DATA.bioExtended}
            </p>
            <Link to="/about" className="btn-secondary inline-flex items-center gap-2 font-black">
              Read Full Bio <ChevronRight size={20} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

// Issues Section
const IssuesSection = ({ issues }) => {
  // Use provided issues from API or fall back to static data
  const displayIssues = issues && issues.length > 0 ? issues : CAMPAIGN_DATA.issues;
  
  return (
    <section data-testid="issues-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="issues-title">
            Issues That Actually Matter
          </h2>
          <p className="text-lg text-gray-600 font-body max-w-2xl mx-auto">
            {CAMPAIGN_DATA.subSlogan}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {displayIssues.map((issue, index) => {
            // Handle both static icons (components) and dynamic icons (strings)
            const IconComponent = typeof issue.icon === 'string' ? (ICON_MAP[issue.icon] || Shield) : issue.icon;
            return (
              <div 
                key={issue.id || index} 
                className="bg-white p-8 rounded-sm shadow-sm card-hover text-center"
                data-testid={`issue-card-${index}`}
              >
                <div className="issue-icon">
                  <IconComponent className="w-8 h-8 text-black" />
                </div>
                <h3 className="font-heading text-xl uppercase text-black mb-3">{issue.title}</h3>
                <p className="text-gray-600 font-body text-sm">{issue.description || issue.desc}</p>
              </div>
            );
          })}
        </div>
        
        <div className="text-center mt-12">
          <p className="text-2xl font-heading uppercase text-campaign-green">{CAMPAIGN_DATA.endSlogan}</p>
          <a 
            href={CAMPAIGN_DATA.donateUrl} 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block mt-6 bg-campaign-green text-white px-10 py-4 text-lg font-heading uppercase tracking-wide hover:bg-campaign-green-hover transition-colors"
          >
            Donate
          </a>
        </div>
      </div>
    </section>
  );
};

// Video Section - Commented out for now (no video available)
// const VideoSection = () => {
//   if (!CAMPAIGN_DATA.youtube) return null;
//   return (
//     <section data-testid="video-section" className="py-20 bg-black">
//       <div className="container-campaign">
//         <div className="text-center mb-12">
//           <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-4" data-testid="video-title">
//             Campaign Announcement
//           </h2>
//         </div>
//         <div className="max-w-4xl mx-auto">
//           <div className="video-container rounded-sm overflow-hidden shadow-2xl">
//             <iframe 
//               src={CAMPAIGN_DATA.youtube}
//               title="Brandon Neider Campaign"
//               frameBorder="0"
//               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
//               allowFullScreen
//               data-testid="youtube-video"
//             />
//           </div>
//         </div>
//       </div>
//     </section>
//   );
// };

// Photo Gallery Section
const GallerySection = ({ photos }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showSlideshow, setShowSlideshow] = useState(false);
  
  // Use provided photos or fall back to static gallery
  const galleryPhotos = photos && photos.length > 0 ? photos : CAMPAIGN_DATA.gallery;
  
  // Show only first 18 photos (6 rows x 3 columns) in the grid
  const maxVisible = 18;
  const visiblePhotos = galleryPhotos.slice(0, maxVisible);
  const hasMore = galleryPhotos.length > maxVisible;
  
  const openSlideshow = (index) => {
    setSelectedIndex(index);
    setShowSlideshow(true);
  };
  
  const closeSlideshow = () => {
    setShowSlideshow(false);
    setSelectedIndex(null);
  };
  
  const goToPrev = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === 0 ? galleryPhotos.length - 1 : prev - 1));
  };
  
  const goToNext = (e) => {
    e.stopPropagation();
    setSelectedIndex((prev) => (prev === galleryPhotos.length - 1 ? 0 : prev + 1));
  };
  
  // Handle keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSlideshow) return;
      if (e.key === 'ArrowLeft') goToPrev(e);
      if (e.key === 'ArrowRight') goToNext(e);
      if (e.key === 'Escape') closeSlideshow();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSlideshow]);

  return (
    <section data-testid="gallery-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="gallery-title">
            On The Campaign Trail
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Bebe Ahmed meeting with voters and community leaders across District 14
          </p>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {visiblePhotos.map((photo, index) => (
            <div 
              key={photo.id || index}
              className="relative aspect-square overflow-hidden rounded-sm cursor-pointer group bg-gray-200"
              onClick={() => openSlideshow(index)}
              data-testid={`gallery-image-${index}`}
            >
              <img 
                src={resolveImageUrl(photo.url)} 
                alt={photo.caption || 'Campaign photo'}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                  e.target.insertAdjacentHTML('afterend', '<div class="text-gray-400 text-center p-4"><svg class="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg><span class="text-xs">Image unavailable</span></div>');
                }}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                <Camera className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </div>
          ))}
        </div>
        
        {/* See More Button */}
        {hasMore && (
          <div className="text-center mt-8">
            <button
              onClick={() => openSlideshow(0)}
              className="bg-black text-white px-8 py-3 font-heading uppercase tracking-wide hover:bg-gray-800 transition-colors inline-flex items-center gap-2"
              data-testid="gallery-see-more"
            >
              <Camera size={20} />
              See All {galleryPhotos.length} Photos
            </button>
          </div>
        )}
      </div>
      
      {/* Full-screen Slideshow */}
      {showSlideshow && selectedIndex !== null && (
        <div 
          className="fixed inset-0 bg-black z-50 flex flex-col"
          onClick={closeSlideshow}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-black/50">
            <span className="text-white font-heading">
              {selectedIndex + 1} / {galleryPhotos.length}
            </span>
            <button 
              className="text-white hover:text-gray-300 transition-colors"
              onClick={closeSlideshow}
              data-testid="slideshow-close"
            >
              <X size={32} />
            </button>
          </div>
          
          {/* Main Content */}
          <div className="flex-1 flex items-center justify-center relative px-4" onClick={(e) => e.stopPropagation()}>
            {/* Previous Button */}
            <button
              onClick={goToPrev}
              className="absolute left-4 md:left-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="slideshow-prev"
            >
              <ChevronLeft size={32} />
            </button>
            
            {/* Image */}
            <div className="max-w-5xl max-h-[75vh] flex items-center justify-center">
              <img 
                src={resolveImageUrl(galleryPhotos[selectedIndex].url)} 
                alt={galleryPhotos[selectedIndex].caption || 'Campaign photo'}
                className="max-w-full max-h-[75vh] object-contain"
              />
            </div>
            
            {/* Next Button */}
            <button
              onClick={goToNext}
              className="absolute right-4 md:right-8 z-10 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-colors"
              data-testid="slideshow-next"
            >
              <ChevronRight size={32} />
            </button>
          </div>
          
          {/* Caption */}
          <div className="px-6 py-4 bg-black/50 text-center">
            <p className="text-white font-body">
              {galleryPhotos[selectedIndex].caption || 'On the campaign trail'}
            </p>
          </div>
          
          {/* Thumbnail Strip */}
          <div className="bg-black/80 px-4 py-3 overflow-x-auto">
            <div className="flex gap-2 justify-center min-w-max">
              {galleryPhotos.map((photo, index) => (
                <button
                  key={photo.id || index}
                  onClick={(e) => { e.stopPropagation(); setSelectedIndex(index); }}
                  className={`w-16 h-16 rounded overflow-hidden flex-shrink-0 transition-all ${
                    index === selectedIndex ? 'ring-2 ring-white scale-110' : 'opacity-50 hover:opacity-100'
                  }`}
                >
                  <img 
                    src={resolveImageUrl(photo.url)} 
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Events Section
const EventsSection = ({ events, showAll = false }) => {
  const displayEvents = showAll ? events : events.slice(0, 3);
  
  return (
    <section data-testid="events-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="events-title">
            Upcoming Events
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Join us on the campaign trail
          </p>
        </div>
        
        {displayEvents.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-sm">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-body">No upcoming events at this time. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayEvents.map((event, index) => (
              <Link 
                to={`/events/${event.id}`}
                key={event.id || index} 
                className="bg-gray-50 rounded-sm overflow-hidden card-hover block"
                data-testid={`event-card-${index}`}
              >
                {/* Event Thumbnail - Natural aspect ratio */}
                {event.image_url && (
                  <div className="w-full">
                    <img 
                      src={event.image_url} 
                      alt={event.title}
                      className="w-full h-auto"
                    />
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center gap-2 text-campaign-green font-semibold mb-2">
                    <Calendar size={18} />
                    <span>{event.date}</span>
                    {event.time && <span>| {event.time}</span>}
                  </div>
                  <h3 className="font-heading text-xl text-black uppercase mb-2">{event.title}</h3>
                  <p className="text-gray-600 font-body mb-4">{event.description}</p>
                  {event.location && (
                    <p className="text-gray-500 text-sm mb-4 flex items-center gap-1">
                      <MapPin size={14} /> {event.location}
                    </p>
                  )}
                  <span className="inline-block bg-black text-white font-bold uppercase tracking-wider py-2 px-6 text-sm hover:bg-gray-800 transition-colors">
                    View Details
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
        
        {!showAll && events.length > 3 && (
          <div className="text-center mt-8">
            <Link to="/events" className="btn-secondary inline-flex items-center gap-2">
              View All Events <ChevronRight size={20} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

// News Section
const NewsSection = ({ news, showAll = false }) => {
  const [selectedArticle, setSelectedArticle] = useState(null);
  const displayNews = showAll ? news : news.slice(0, 3);
  
  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };
  
  return (
    <section data-testid="news-section" className="py-20 bg-gray-50">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="news-title">
            In The News
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Latest coverage and updates from the campaign
          </p>
        </div>
        
        {displayNews.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-sm">
            <Newspaper className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-body">No news articles yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {displayNews.map((article, index) => {
              const isOriginal = article.content_type === 'original';
              const videoId = getYouTubeId(article.video_url);
              
              return (
                <div 
                  key={article.id || index} 
                  className="bg-white rounded-sm overflow-hidden shadow-sm card-hover"
                  data-testid={`news-card-${index}`}
                >
                  {/* Video thumbnail or image */}
                  {videoId ? (
                    <div 
                      className="relative w-full h-48 bg-black cursor-pointer group"
                      onClick={() => setSelectedArticle(article)}
                    >
                      <img 
                        src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`} 
                        alt={article.title} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                        <div className="w-16 h-16 bg-campaign-green rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-white ml-1" />
                        </div>
                      </div>
                      {isOriginal && (
                        <span className="absolute top-2 left-2 bg-campaign-green text-white text-xs px-2 py-1 font-semibold uppercase">
                          Video
                        </span>
                      )}
                    </div>
                  ) : article.image_url ? (
                    <div className="relative">
                      <img src={resolveImageUrl(article.image_url)} alt={article.title} className="w-full h-48 object-cover" />
                      {isOriginal && (
                        <span className="absolute top-2 left-2 bg-campaign-green text-white text-xs px-2 py-1 font-semibold uppercase">
                          Campaign Update
                        </span>
                      )}
                    </div>
                  ) : isOriginal && (
                    <div className="w-full h-48 bg-black flex items-center justify-center">
                      <span className="text-white font-heading text-2xl uppercase">Campaign Update</span>
                    </div>
                  )}
                  
                  <div className="p-6">
                    <div className="text-sm text-gray-500 mb-2">
                      {article.published_date} {!isOriginal && article.source && `| ${article.source}`}
                    </div>
                    <h3 className="font-heading text-xl text-black uppercase mb-2">{article.title}</h3>
                    <p className="text-gray-600 font-body mb-4">{article.summary}</p>
                    
                    {isOriginal ? (
                      <button 
                        onClick={() => setSelectedArticle(article)}
                        className="text-campaign-green font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        {videoId ? 'Watch Video' : 'Read More'} <ChevronRight size={14} />
                      </button>
                    ) : article.link && (
                      <a 
                        href={article.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-campaign-green font-semibold hover:underline inline-flex items-center gap-1"
                      >
                        Read More <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {!showAll && news.length > 3 && (
          <div className="text-center mt-8">
            <Link to="/news" className="btn-secondary inline-flex items-center gap-2">
              View All News <ChevronRight size={20} />
            </Link>
          </div>
        )}
      </div>
      
      {/* Article Modal for Original Content */}
      {selectedArticle && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedArticle(null)}
        >
          <div 
            className="bg-white rounded-sm max-w-4xl w-full max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <span className="text-sm text-gray-500">{selectedArticle.published_date}</span>
              <button 
                onClick={() => setSelectedArticle(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Video embed */}
            {getYouTubeId(selectedArticle.video_url) && (
              <div className="aspect-video w-full bg-black">
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(selectedArticle.video_url)}?autoplay=1`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedArticle.title}
                />
              </div>
            )}
            
            {/* Content */}
            <div className="p-6 md:p-10">
              <h2 className="font-heading text-3xl md:text-4xl text-black uppercase mb-4">
                {selectedArticle.title}
              </h2>
              
              {selectedArticle.image_url && !getYouTubeId(selectedArticle.video_url) && (
                <img 
                  src={resolveImageUrl(selectedArticle.image_url)} 
                  alt={selectedArticle.title}
                  className="w-full max-h-96 object-cover rounded mb-6"
                />
              )}
              
              <div className="prose prose-lg max-w-none">
                {selectedArticle.content ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
                ) : (
                  <p className="text-gray-600 font-body text-lg">{selectedArticle.summary}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Quick Donate Section (appears early on homepage)
const QuickDonateSection = () => {
  return (
    <section data-testid="quick-donate" className="py-12 bg-black">
      <div className="container-campaign">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl text-white uppercase mb-2">
              Support Bebe Today
            </h3>
            <p className="text-gray-400">Every dollar helps bring real representation to District 14</p>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            {CAMPAIGN_DATA.donateAmounts.map((amount) => (
              <a 
                key={amount}
                href={CAMPAIGN_DATA.donateUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-campaign-green hover:bg-campaign-green-hover text-white font-bold py-3 px-6 text-lg transition-colors"
              >
                ${amount}
              </a>
            ))}
            <a 
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="border-2 border-white text-white hover:bg-white hover:text-black font-bold py-3 px-6 text-lg transition-colors"
            >
              Other
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Section
const VideoSection = () => {
  if (!CAMPAIGN_DATA.youtubeVideo) return null;
  
  return (
    <section data-testid="video-section" className="py-20 bg-gray-900">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-4">
            Watch Bebe's Message
          </h2>
          <p className="text-lg text-gray-400 font-body">
            Hear directly from Bebe about her vision for District 14
          </p>
        </div>
        <div className="max-w-4xl mx-auto">
          <div className="video-container rounded-sm overflow-hidden shadow-2xl">
            <iframe 
              src={CAMPAIGN_DATA.youtubeVideo}
              title="Bebe Ahmed Campaign Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="youtube-video"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// Donate CTA Section
const DonateCTA = () => {
  return (
    <section data-testid="donate-cta" className="py-20 bg-campaign-green">
      <div className="container-campaign text-center">
        <h2 className="font-heading text-4xl md:text-5xl text-white uppercase mb-6">
          Support the Campaign
        </h2>
        <p className="text-xl text-white/90 font-body mb-8 max-w-2xl mx-auto">
          Help Bebe Ahmed bring real representation to District 14. Every contribution makes a difference.
        </p>
        
        {/* Quick donate amounts */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          {CAMPAIGN_DATA.donateAmounts.map((amount) => (
            <a 
              key={amount}
              href={CAMPAIGN_DATA.donateUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white text-campaign-green hover:bg-gray-100 font-bold py-3 px-8 text-lg transition-colors"
            >
              ${amount}
            </a>
          ))}
        </div>
        
        <a 
          href={CAMPAIGN_DATA.donateUrl} 
          target="_blank" 
          rel="noopener noreferrer"
          className="inline-block bg-black text-white font-bold uppercase tracking-wider py-4 px-12 text-lg hover:bg-gray-900 transition-colors shadow-lg"
          data-testid="donate-cta-btn"
        >
          Donate Any Amount
        </a>
      </div>
    </section>
  );
};

// Endorsements Section
const EndorsementsSection = ({ endorsements }) => {
  if (!endorsements || endorsements.length === 0) {
    return null;
  }
  
  const orgEndorsements = endorsements.filter(e => e.endorsement_type === 'organization');
  const individualEndorsements = endorsements.filter(e => e.endorsement_type !== 'organization');
  
  return (
    <section data-testid="endorsements-section" className="py-20 bg-white">
      <div className="container-campaign">
        <div className="text-center mb-12">
          <h2 className="font-heading text-4xl md:text-5xl text-black uppercase mb-4" data-testid="endorsements-title">
            Endorsements
          </h2>
          <p className="text-lg text-gray-600 font-body">
            Leaders and organizations who support Bebe Ahmed for Westchester County Legislator
          </p>
        </div>
        
        {/* Organization Endorsements */}
        {orgEndorsements.length > 0 && (
          <div className="mb-16">
            <h3 className="font-heading text-2xl text-black uppercase mb-8 text-center">Endorsed By</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
              {orgEndorsements.map((endorsement, index) => (
                <div 
                  key={endorsement.id || index} 
                  className="bg-gray-50 p-6 rounded-sm card-hover flex flex-col items-center text-center"
                  data-testid={`org-endorsement-${index}`}
                >
                  {endorsement.image_url ? (
                    <img 
                      src={resolveImageUrl(endorsement.image_url)} 
                      alt={endorsement.name}
                      className="h-20 w-auto object-contain mb-4"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded bg-black/10 flex items-center justify-center mb-4">
                      <Building2 className="w-10 h-10 text-black" />
                    </div>
                  )}
                  <h4 className="font-heading text-lg text-black uppercase">{endorsement.name}</h4>
                  {endorsement.title && (
                    <p className="text-gray-500 text-sm mt-1">{endorsement.title}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Individual Endorsements */}
        {individualEndorsements.length > 0 && (
          <div>
            {orgEndorsements.length > 0 && (
              <h3 className="font-heading text-2xl text-black uppercase mb-8 text-center">Individual Endorsements</h3>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {individualEndorsements.map((endorsement, index) => (
                <div 
                  key={endorsement.id || index} 
                  className="bg-gray-50 p-6 rounded-sm card-hover"
                  data-testid={`endorsement-card-${index}`}
                >
                  <div className="flex items-start gap-4">
                    {endorsement.image_url ? (
                      <img 
                        src={resolveImageUrl(endorsement.image_url)} 
                        alt={endorsement.name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-black/10 flex items-center justify-center flex-shrink-0">
                        <User className="w-8 h-8 text-black" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-heading text-lg text-black uppercase">{endorsement.name}</h3>
                      {endorsement.title && (
                        <p className="text-campaign-green font-semibold text-sm">{endorsement.title}</p>
                      )}
                      {endorsement.organization && (
                        <p className="text-gray-500 text-sm">{endorsement.organization}</p>
                      )}
                    </div>
                  </div>
                  {endorsement.quote && (
                    <div className="mt-4 relative">
                      <Quote className="w-6 h-6 text-black/20 absolute -top-1 -left-1" />
                      <p className="text-gray-600 font-body italic pl-5">"{endorsement.quote}"</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Email/Text Signup Section
const SignupSection = () => {
  const [form, setForm] = useState({
    email: '',
    phone: '',
    first_name: '',
    last_name: '',
    wants_email: true,
    wants_text: false,
    sms_consent: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.wants_email && !form.wants_text) {
      setError('Please select at least one option (email or text)');
      return;
    }
    if (form.wants_text && !form.phone) {
      setError('Please enter your phone number to receive text updates');
      return;
    }
    if (form.wants_text && !form.sms_consent) {
      setError('Please agree to receive SMS/MMS text messages to continue');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/subscribe`, form);
      setSuccess(true);
      setForm({ email: '', phone: '', first_name: '', last_name: '', wants_email: true, wants_text: false, sms_consent: false });
    } catch (e) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section data-testid="signup-section" className="py-20 bg-black">
        <div className="container-campaign text-center">
          <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
          <h2 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">
            You're Signed Up!
          </h2>
          <p className="text-lg text-gray-300 font-body">
            Thank you for joining our campaign. We'll keep you updated on our progress.
          </p>
          <button 
            onClick={() => setSuccess(false)}
            className="mt-6 text-white/70 hover:text-white underline"
          >
            Sign up another person
          </button>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="signup-section" className="py-20 bg-black">
      <div className="container-campaign">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white uppercase mb-4">
            Stay Connected
          </h2>
          <p className="text-lg text-gray-300 font-body mb-8">
            Join our email and text list to receive campaign updates, event invitations, and ways to get involved.
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-500/20 border border-red-500 text-red-200 px-4 py-3 rounded text-sm">
                {error}
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="First Name"
                value={form.first_name}
                onChange={(e) => setForm({...form, first_name: e.target.value})}
                className="w-full px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50"
              />
              <input
                type="text"
                placeholder="Last Name"
                value={form.last_name}
                onChange={(e) => setForm({...form, last_name: e.target.value})}
                className="w-full px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50"
              />
            </div>
            
            <input
              type="email"
              placeholder="Email Address *"
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50"
              required
            />
            
            <input
              type="tel"
              placeholder="Phone Number (for text updates)"
              value={form.phone}
              onChange={(e) => setForm({...form, phone: e.target.value})}
              className="w-full px-4 py-3 rounded-sm bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-white/50"
            />
            
            <div className="flex flex-wrap justify-center gap-6 text-white">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.wants_email}
                  onChange={(e) => setForm({...form, wants_email: e.target.checked})}
                  className="w-5 h-5 rounded"
                />
                <span className="flex items-center gap-1">
                  <Mail size={16} /> Email Updates
                </span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.wants_text}
                  onChange={(e) => setForm({...form, wants_text: e.target.checked})}
                  className="w-5 h-5 rounded"
                />
                <span className="flex items-center gap-1">
                  <Phone size={16} /> Text Updates (SMS/MMS)
                </span>
              </label>
            </div>
            
            {/* SMS Opt-In Disclosure - Always visible */}
            <div className="bg-white/5 border border-white/20 rounded-sm p-4 text-left text-sm text-gray-300">
              <p className="mb-3">
                By entering your phone number and selecting to opt in, you consent to join a recurring SMS/MMS text messaging program that will provide alerts, donation requests, updates, and other important information. By participating, you agree to the <Link to="/terms-and-conditions" className="text-campaign-green-light hover:underline">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-campaign-green-light hover:underline">Privacy Policy</Link> for autodialed messages from Bebe for Westchester to the number you provide. Message frequency varies. Msg & data rates may apply. Reply HELP for help or STOP to opt out at any time. Email us at <a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="text-campaign-green-light hover:underline">{CAMPAIGN_DATA.contact.email}</a> for assistance. SMS opt-in information and consent are not sold, rented, or shared with third parties for their own marketing purposes.
              </p>
              
              <div className="bg-white/5 p-3 rounded-sm mb-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.sms_consent}
                    onChange={(e) => setForm({...form, sms_consent: e.target.checked, wants_text: e.target.checked ? true : form.wants_text})}
                    className="w-5 h-5 rounded mt-0.5"
                  />
                  <span><strong>I agree to receive recurring SMS/MMS text messages from Bebe for Westchester.</strong></span>
                </label>
                <p className="mt-2 text-xs text-gray-400 ml-7">
                  By entering your mobile phone number, checking this optional box, and submitting this form, you consent to join a recurring SMS/MMS text messaging program from Bebe for Westchester. Messages may be sent using an autodialer to the number you provide.
                </p>
              </div>
              
              <ul className="space-y-2 text-xs text-gray-400">
                <li><strong>Privacy Policy instructions:</strong> To learn how we collect, use, and protect your information, click the <Link to="/privacy-policy" className="text-campaign-green-light hover:underline">Privacy Policy</Link> link in this form. The Privacy Policy is also available under the Legal section in the website footer.</li>
                <li><strong>How to opt out:</strong> Reply <strong>STOP</strong> at any time to opt out and stop receiving messages.</li>
                <li><strong>How to receive help:</strong> Reply <strong>HELP</strong> for help or email <a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="text-campaign-green-light hover:underline">{CAMPAIGN_DATA.contact.email}</a> for assistance.</li>
                <li><strong>Message and data rates:</strong> Msg & data rates may apply.</li>
                <li><strong>Message frequency:</strong> Message frequency varies.</li>
                <li><strong>Types of messages:</strong> Messages may include campaign alerts, donation and fundraising requests, campaign updates, voter outreach, candidate information, volunteer opportunities, event invitations and reminders, polling questions, surveys, get-out-the-vote reminders, election information, public service announcements, and other important campaign information.</li>
              </ul>
              
              <p className="mt-3 text-xs text-gray-400">
                By participating, you agree to the <Link to="/terms-and-conditions" className="text-campaign-green-light hover:underline">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-campaign-green-light hover:underline">Privacy Policy</Link>. Consent is not a condition of making a donation, volunteering, supporting the campaign, purchasing anything, or using this website.
              </p>
              
              <p className="mt-2 text-xs text-gray-400">
                SMS opt-in information and consent are not sold, rented, or shared with third parties for their own marketing purposes.
              </p>
              
              <p className="mt-3 text-xs text-gray-500 font-semibold">
                Paid for by Bebe For Westchester.
              </p>
            </div>
            
            <button
              type="submit"
              disabled={loading}
              className="w-full sm:w-auto bg-campaign-green hover:bg-campaign-green-hover text-white font-black uppercase tracking-wider py-4 px-12 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 mx-auto"
              data-testid="signup-submit"
            >
              {loading ? 'Signing Up...' : (
                <>
                  <Send size={18} /> Sign Up
                </>
              )}
            </button>
          </form>
          
          <p className="text-gray-400 text-sm mt-6">
            We respect your privacy. Unsubscribe at any time. View our <Link to="/terms-and-conditions" className="text-campaign-green-light hover:underline">Terms & Conditions</Link> and <Link to="/privacy-policy" className="text-campaign-green-light hover:underline">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </section>
  );
};

// Home Page
const Home = () => {
  usePageTitle(null); // Use base title for home
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [issues, setIssues] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, newsRes, endorsementsRes, galleryRes, issuesRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/news`),
          axios.get(`${API}/endorsements`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/issues`)
        ]);
        setEvents(eventsRes.data);
        setNews(newsRes.data);
        setEndorsements(endorsementsRes.data);
        setGalleryPhotos(galleryRes.data);
        setIssues(issuesRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
        // Set placeholder data
        setEvents([{
          id: 'placeholder',
          title: 'Campaign Fundraiser',
          description: 'Join us for an evening reception to support Bebe Ahmed for Westchester County Legislator.',
          date: 'TBD',
          time: 'TBD',
          location: 'Westchester County, NY',
          rsvp_link: '#'
        }]);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <QuickDonateSection />
      <MeetBrandonSection />
      <IssuesSection issues={issues} />
      <VideoSection />
      <EventsSection events={events} />
      <GallerySection photos={galleryPhotos} />
      <EndorsementsSection endorsements={endorsements} />
      <SignupSection />
      <NewsSection news={news} />
      <DonateCTA />
    </div>
  );
};

// About Page
const AboutPage = () => {
  usePageTitle("About Bebe");
  return (
    <div className="pt-20" data-testid="about-page">
      {/* Hero */}
      <section className="py-20 bg-black">
        <div className="container-campaign">
          <p className="text-campaign-green font-black uppercase tracking-wider mb-2">{CAMPAIGN_DATA.tagline}</p>
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">About Bebe</h1>
        </div>
      </section>
      
      {/* Content */}
      <section className="py-20">
        <div className="container-campaign">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="font-heading text-3xl text-black uppercase mb-6">Meet Bebe Ahmed</h2>
              <p className="text-lg text-gray-700 font-body leading-relaxed mb-6">
                {CAMPAIGN_DATA.bio}
              </p>
              <p className="text-gray-600 font-body leading-relaxed mb-6">
                {CAMPAIGN_DATA.bioShort}
              </p>
              <p className="text-gray-600 font-body leading-relaxed mb-6">
                {CAMPAIGN_DATA.bioExtended}
              </p>
              
              {/* Real Representation */}
              <div className="bg-gray-50 p-8 rounded-sm mb-8">
                <h3 className="font-heading text-2xl text-black uppercase mb-4">Real Representation for District 14</h3>
                <p className="text-gray-700 mb-4">District 14 deserves a voice that understands the impact of county decisions:</p>
                <ul className="space-y-2 mb-4 text-gray-700">
                  <li className="flex items-center gap-2"><span className="text-campaign-green">•</span> Affordable property taxes</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-green">•</span> Safe streets and communities</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-green">•</span> Transparent county government</li>
                  <li className="flex items-center gap-2"><span className="text-campaign-green">•</span> Fair treatment for working families</li>
                </ul>
                <p className="text-gray-700 font-semibold">It's time for real change in Westchester.</p>
              </div>

              <h3 className="font-heading text-2xl text-black uppercase mb-4">The Issues</h3>
              
              {/* Affordability & Tax Relief */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <TrendingDown className="w-6 h-6 text-campaign-green mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-black font-black text-lg">Affordability & Tax Relief</strong>
                    <p className="text-gray-600 mt-1">Westchester families are paying more and getting less. Property taxes are unsustainable.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Fight for responsible budgeting</li>
                      <li>• Smarter use of tax dollars</li>
                      <li>• Keep families in their communities</li>
                      <li>• Sustainable property taxes</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Public Safety */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <Shield className="w-6 h-6 text-campaign-green mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-black font-black text-lg">Public Safety</strong>
                    <p className="text-gray-600 mt-1">Public safety must come first. Residents deserve transparency and leadership that puts community safety ahead of politics.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Support law enforcement</li>
                      <li>• Demand accountability at every level</li>
                      <li>• Protect law-abiding residents</li>
                      <li>• Community-first policing</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Cost of Living */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-2">
                  <MapPin className="w-6 h-6 text-campaign-green mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-black font-black text-lg">Cost of Living</strong>
                    <p className="text-gray-600 mt-1">Unchecked energy costs are crushing our community. Rising Con Edison bills are driving up expenses for everyone.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Stand up to unfair rate hikes</li>
                      <li>• Demand transparency from utilities</li>
                      <li>• Push back against costly mandates</li>
                      <li>• Protect working families</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Real Representation */}
              <div className="mb-8">
                <div className="flex items-start gap-3 mb-2">
                  <Users className="w-6 h-6 text-campaign-green mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-black font-black text-lg">Real Representation</strong>
                    <p className="text-gray-600 mt-1">District 14 deserves a voice that understands the impact of county decisions.</p>
                    <ul className="mt-2 space-y-1 text-gray-700">
                      <li>• Represent every neighborhood</li>
                      <li>• Mount Vernon to McLean Avenue</li>
                      <li>• Honest, common-sense leadership</li>
                      <li>• Make government work for the people</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Real Change */}
              <div className="bg-black text-white p-8 rounded-sm mb-8">
                <h3 className="font-heading text-2xl uppercase mb-4">It's Time for Real Change.</h3>
                <p className="text-gray-200 leading-relaxed">
                  Bebe has lived in Westchester for over 26 years, raising her family and paying property taxes for decades. As a retired NYC public school teacher and mother of two, she knows what these policies mean for families. It's time to bring accountability, affordability, and transparency back to county government.
                </p>
              </div>
              
              {/* CTA */}
              <div className="bg-campaign-green text-white p-8 rounded-sm">
                <h3 className="font-heading text-2xl uppercase mb-4">Join the Movement</h3>
                <p className="text-white/90 mb-4">This campaign is about:</p>
                <ul className="space-y-2 text-white/90 mb-4">
                  <li className="flex items-center gap-2">✓ Real representation for District 14</li>
                  <li className="flex items-center gap-2">✓ Standing up for working families</li>
                  <li className="flex items-center gap-2">✓ Accountability in county government</li>
                  <li className="flex items-center gap-2">✓ Common sense leadership</li>
                </ul>
                <a 
                  href={CAMPAIGN_DATA.donateUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-white text-campaign-green px-8 py-3 font-heading uppercase tracking-wide hover:bg-gray-100 transition-colors"
                >
                  Donate Now
                </a>
              </div>
            </div>
            
            <div>
              <img 
                src={CAMPAIGN_DATA.images.heroMobile}
                alt="Bebe Ahmed" 
                className="w-full rounded-sm shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>
      
      <DonateCTA />
    </div>
  );
};

// Events Page
const EventsPage = () => {
  usePageTitle("Events");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const res = await axios.get(`${API}/events`);
        setEvents(res.data);
      } catch (e) {
        console.error("Error fetching events:", e);
        setEvents([{
          id: 'placeholder',
          title: 'Campaign Fundraiser',
          description: 'Join us for an evening reception to support Bebe Ahmed for Westchester County Legislator.',
          date: 'TBD',
          time: 'TBD',
          location: 'Westchester County, NY',
          rsvp_link: '#'
        }]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  return (
    <div className="pt-28" data-testid="events-page">
      <section className="py-20 bg-black">
        <div className="container-campaign">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">Events</h1>
        </div>
      </section>
      
      <EventsSection events={events} showAll={true} />
      <DonateCTA />
    </div>
  );
};

// Event Detail Page
const EventDetailPage = () => {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${API}/events/${eventId}`);
        setEvent(res.data);
        document.title = `${res.data.title} | Bebe Ahmed for Westchester`;
      } catch (e) {
        console.error("Error fetching event:", e);
        navigate('/events');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId, navigate]);

  if (loading) {
    return (
      <div className="pt-28 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-campaign-green mx-auto mb-4"></div>
          <p className="text-gray-600">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="pt-28 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Event not found</p>
          <Link to="/events" className="text-campaign-green hover:underline">
            ← Back to Events
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28" data-testid="event-detail-page">
      {/* Header */}
      <section className="py-12 bg-black">
        <div className="container-campaign">
          <Link to="/events" className="text-gray-400 hover:text-white transition-colors inline-flex items-center gap-2 mb-4">
            <ChevronLeft size={20} /> Back to Events
          </Link>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white uppercase">{event.title}</h1>
        </div>
      </section>
      
      {/* Event Content */}
      <section className="py-12 bg-white">
        <div className="container-campaign">
          <div className="max-w-4xl mx-auto">
            {/* Full Width Flyer/Image */}
            {event.image_url && (
              <div className="mb-8">
                <img 
                  src={event.image_url} 
                  alt={event.title}
                  className="w-full h-auto rounded-sm shadow-lg"
                />
              </div>
            )}
            
            {/* Event Details */}
            <div className="bg-gray-50 p-8 rounded-sm mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="bg-campaign-green text-white p-3 rounded-sm">
                    <Calendar size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 uppercase tracking-wider">Date & Time</p>
                    <p className="text-lg font-semibold text-black">{event.date}</p>
                    {event.time && <p className="text-gray-600">{event.time}</p>}
                  </div>
                </div>
                
                {event.location && (
                  <div className="flex items-start gap-4">
                    <div className="bg-campaign-green text-white p-3 rounded-sm">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 uppercase tracking-wider">Location</p>
                      <p className="text-lg font-semibold text-black">{event.location}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Description */}
            <div className="mb-8">
              <h2 className="font-heading text-2xl text-black uppercase mb-4">About This Event</h2>
              <p className="text-gray-700 font-body leading-relaxed text-lg">{event.description}</p>
            </div>
            
            {/* RSVP Button */}
            {event.rsvp_link && (
              <div className="text-center">
                <a 
                  href={event.rsvp_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-campaign-green text-white font-heading uppercase tracking-wider py-4 px-12 text-lg hover:bg-campaign-green-hover transition-colors"
                >
                  RSVP Now
                </a>
              </div>
            )}
          </div>
        </div>
      </section>
      
      <DonateCTA />
    </div>
  );
};

// News Page
const NewsPage = () => {
  usePageTitle("In The News");
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const res = await axios.get(`${API}/news`);
        setNews(res.data);
      } catch (e) {
        console.error("Error fetching news:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  return (
    <div className="pt-28" data-testid="news-page">
      <section className="py-20 bg-black">
        <div className="container-campaign">
          <h1 className="font-heading text-5xl md:text-7xl text-white uppercase">In The News</h1>
        </div>
      </section>
      
      <NewsSection news={news} showAll={true} />
      <DonateCTA />
    </div>
  );
};

// Terms & Conditions Page
const TermsPage = () => {
  usePageTitle("Terms & Conditions");
  
  return (
    <div className="pt-28" data-testid="terms-page">
      <section className="py-12 bg-black">
        <div className="container-campaign">
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase">Terms & Conditions</h1>
        </div>
      </section>
      
      <section className="py-12 bg-white">
        <div className="container-campaign">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <p className="text-gray-500 mb-8"><strong>Last Updated: September 29, 2026</strong></p>
            
            <p className="text-gray-700 mb-6">
              These Terms & Conditions apply to SMS/MMS text messaging and other campaign communications from <strong>Bebe Ahmed</strong>.
            </p>
            
            <p className="text-gray-700 mb-8">
              For information about how Bebe Ahmed collects, uses, and protects information, please review our <Link to="/privacy-policy" className="text-campaign-green hover:underline font-semibold">Privacy Policy</Link>.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">SMS/MMS Text Messaging Terms</h2>
            
            <p className="text-gray-700 mb-4">
              By entering your phone number and selecting to opt in, you agree to receive recurring SMS/MMS text messages from Bebe Ahmed, including campaign alerts, donation requests, updates, voter outreach, event invitations, polling questions, get-out-the-vote reminders, election information, and other important campaign information.
            </p>
            
            <p className="text-gray-700 mb-4">
              <strong>Message frequency varies. Msg & data rates may apply.</strong>
            </p>
            
            <p className="text-gray-700 mb-4">
              Bebe Ahmed does not charge for text messaging services, but your mobile carrier's standard messaging, data, and other rates and fees may apply. Please contact your carrier for details about your mobile plan.
            </p>
            
            <p className="text-gray-700 mb-4">
              You may opt out at any time by replying <strong>STOP</strong> to any text message. You may also reply <strong>HELP</strong> for help.
            </p>
            
            <p className="text-gray-700 mb-4">
              For assistance, reply HELP to any message or contact Bebe Ahmed at:
            </p>
            
            <p className="text-gray-700 mb-4">
              <strong><a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="text-campaign-green hover:underline">{CAMPAIGN_DATA.contact.email}</a></strong>
            </p>
            
            <p className="text-gray-700 mb-8">
              Consent to receive SMS/MMS text messages is not a condition of making a donation, volunteering, supporting the campaign, or using this website.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">Mobile Opt-In Data</h2>
            
            <p className="text-gray-700 mb-4">
              Bebe Ahmed does not sell, rent, or share mobile opt-in data or SMS consent with third parties for their own marketing purposes. Mobile phone numbers and SMS consent information may be shared with service providers only as necessary to operate campaign communications, comply with law, or support campaign operations.
            </p>
            
            <p className="text-gray-700 mb-4">
              Carriers are not liable for delayed or undelivered messages.
            </p>
            
            <p className="text-gray-700 mb-8">
              By participating in the Bebe Ahmed SMS/MMS messaging program, you agree to these Terms & Conditions and the <Link to="/privacy-policy" className="text-campaign-green hover:underline font-semibold">Privacy Policy</Link>.
            </p>
            
            <div className="bg-gray-100 p-6 rounded-sm mt-8">
              <p className="text-gray-700 font-semibold">Paid for by Bebe For Westchester.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => {
  usePageTitle("Privacy Policy");
  
  return (
    <div className="pt-28" data-testid="privacy-page">
      <section className="py-12 bg-black">
        <div className="container-campaign">
          <h1 className="font-heading text-4xl md:text-5xl text-white uppercase">Privacy Policy</h1>
        </div>
      </section>
      
      <section className="py-12 bg-white">
        <div className="container-campaign">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <p className="text-gray-500 mb-8"><strong>Last Updated: September 29, 2026</strong></p>
            
            <p className="text-gray-700 mb-6">
              This privacy notice for <strong>Bebe Ahmed</strong> ("committee," "we," "us," or "our") explains how and why we may collect, store, use, and share information when you interact with our campaign, website, forms, communications, events, donation pages, volunteer opportunities, SMS/MMS text messaging program, email communications, or other campaign-related activities.
            </p>
            
            <p className="text-gray-700 mb-4">This Privacy Policy applies when you:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Visit our website or any website of ours that links to this Privacy Policy</li>
              <li>Register for events</li>
              <li>Sign up to receive campaign updates</li>
              <li>Volunteer or request more information</li>
              <li>Make a donation</li>
              <li>Engage with us through SMS/MMS text messages, email, forms, marketing, events, or other campaign communications</li>
            </ul>
            
            <p className="text-gray-700 mb-8">
              Reading this Privacy Policy will help you understand how Bebe Ahmed collects, uses, and protects information. If you do not agree with this Privacy Policy, please do not use our website, forms, communications, or services.
            </p>
            
            <p className="text-gray-700 mb-8">
              If you have questions or concerns, please contact us at <strong><a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="text-campaign-green hover:underline">{CAMPAIGN_DATA.contact.email}</a></strong>.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">What Personal Information Do We Collect?</h2>
            
            <p className="text-gray-700 mb-4">
              When you visit, use, or interact with our website, forms, events, donation pages, volunteer opportunities, SMS/MMS text messaging program, email communications, or other campaign services, we may collect personal information depending on how you interact with Bebe Ahmed.
            </p>
            
            <h3 className="font-heading text-xl text-black uppercase mt-6 mb-3">Personal Information You Provide</h3>
            <p className="text-gray-700 mb-2">The personal information we collect may include:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li>Name</li>
              <li>Phone number</li>
              <li>Mobile phone number</li>
              <li>Email address</li>
              <li>Mailing address</li>
              <li>Event registration information</li>
              <li>Volunteer or supporter information</li>
              <li>Donation-related information</li>
              <li>Messages or responses you send to us</li>
            </ul>
            
            <h3 className="font-heading text-xl text-black uppercase mt-6 mb-3">Technical, Device, and Security Information</h3>
            <p className="text-gray-700 mb-6">
              When you access our website, we and the service providers that operate and protect it may automatically collect limited technical information. This information may include your Internet Protocol (IP) address; request date and time; pages, files, and links requested; referring website or domain; browser type and version; operating system; device type; user-agent string; preferred language; time zone; screen or viewport dimensions when available; approximate geographic region derived from an IP address; network or autonomous system number (ASN); connection protocol; response status; security-event information; bot or automated-traffic indicators; and similar diagnostic metadata.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">SMS/MMS Text Messaging</h2>
            
            <p className="text-gray-700 mb-4">
              If you provide your mobile phone number and opt in to receive text messages, you agree to receive recurring SMS/MMS text messages from Bebe Ahmed. Messages may include campaign alerts, donation requests, updates, voter outreach, event invitations, polling questions, get-out-the-vote reminders, election information, and other important campaign information.
            </p>
            
            <p className="text-gray-700 mb-4">
              <strong>Message frequency varies. Msg & data rates may apply. Reply HELP for help. Reply STOP to opt out at any time.</strong>
            </p>
            
            <p className="text-gray-700 mb-4">
              Consent to receive SMS/MMS text messages is not a condition of making a donation, volunteering, supporting the campaign, or using this website.
            </p>
            
            <p className="text-gray-700 mb-8">
              Bebe Ahmed does not sell, rent, or share mobile opt-in data or SMS consent with third parties for their own marketing purposes. Mobile phone numbers and SMS consent information may be shared with service providers only as necessary to operate campaign communications, comply with law, or support campaign operations.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 mb-2">Bebe Ahmed may use the information we collect to:</p>
            <ul className="list-disc pl-6 mb-8 text-gray-700">
              <li>Communicate with voters, supporters, volunteers, donors, and community members</li>
              <li>Send campaign updates, voter outreach, event invitations, volunteer opportunities, fundraising reminders, polling questions, get-out-the-vote reminders, election information, and public service announcements</li>
              <li>Respond to questions, requests, replies, or other communications</li>
              <li>Process event registrations, volunteer sign-ups, supporter forms, and donations</li>
              <li>Improve our website, forms, communications, and campaign operations</li>
              <li>Maintain website security and investigate malicious, fraudulent, abusive, or automated activity</li>
              <li>Diagnose errors and ensure compatibility across browsers, operating systems, screen sizes, networks, and devices</li>
              <li>Comply with legal, reporting, regulatory, security, and campaign-related obligations</li>
              <li>Protect against misuse, fraud, unauthorized activity, or security incidents</li>
            </ul>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">How We Share Information</h2>
            <p className="text-gray-700 mb-4">
              <strong>Bebe Ahmed does not sell personal information.</strong>
            </p>
            <p className="text-gray-700 mb-4">
              We may share information with service providers, vendors, consultants, or other partners who help us operate the campaign, website, forms, SMS/MMS messaging, email communications, donation processing, event registration, data management, compliance, security, or other campaign operations.
            </p>
            <p className="text-gray-700 mb-8">
              Mobile opt-in data and SMS consent are not sold, rented, or shared with third parties for their own marketing purposes.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">How Do We Keep Your Information Safe?</h2>
            <p className="text-gray-700 mb-8">
              We use reasonable organizational, technical, and administrative measures designed to protect personal information. However, no electronic transmission over the internet or information storage system can be guaranteed to be 100% secure. We cannot promise or guarantee that unauthorized third parties will never be able to defeat our security measures or improperly collect, access, steal, or modify information.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">Your Choices</h2>
            <p className="text-gray-700 mb-4">
              You may opt out of SMS/MMS text messages at any time by replying <strong>STOP</strong> to any text message from Bebe Ahmed. You may reply <strong>HELP</strong> for help.
            </p>
            <p className="text-gray-700 mb-8">
              You may also contact us at <strong><a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="text-campaign-green hover:underline">{CAMPAIGN_DATA.contact.email}</a></strong> with questions or requests regarding your information.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">Third-Party Links</h2>
            <p className="text-gray-700 mb-8">
              Our website, text messages, emails, or other campaign communications may include links to third-party websites, donation platforms, event pages, social media platforms, voter information resources, or other websites. Bebe Ahmed is not responsible for the privacy practices, content, or policies of third-party websites.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 mb-8">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. Changes and clarifications take effect when posted.
            </p>
            
            <h2 className="font-heading text-2xl text-black uppercase mt-8 mb-4">Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about this Privacy Policy or would like to access, correct, amend, or delete personal information you have provided, contact Bebe Ahmed at:
            </p>
            <p className="text-gray-700 mb-8">
              <strong><a href={`mailto:${CAMPAIGN_DATA.contact.email}`} className="text-campaign-green hover:underline">{CAMPAIGN_DATA.contact.email}</a></strong>
            </p>
            
            <div className="bg-gray-100 p-6 rounded-sm mt-8">
              <p className="text-gray-700 font-semibold">Paid for by Bebe For Westchester.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Admin Login Page
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        auth: { username, password }
      });
      onLogin({ username, password });
    } catch (e) {
      setError('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100" data-testid="admin-login">
      <div className="bg-white p-8 rounded-sm shadow-lg w-full max-w-md">
        <div className="text-center mb-8">
          <img src={CAMPAIGN_DATA.logoSvg} alt="Logo" className="h-20 mx-auto mb-4 logo-light" />
          <h1 className="font-heading text-2xl text-black uppercase">Admin Login</h1>
        </div>
        
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label className="block text-gray-700 font-black mb-2">Username</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              required
              data-testid="admin-username"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-gray-700 font-black mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              required
              data-testid="admin-password"
            />
          </div>
          
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white font-black uppercase py-3 hover:bg-black-light transition-colors disabled:opacity-50"
            data-testid="admin-submit"
          >
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
        
        <div className="mt-4 text-center">
          <Link to="/" className="text-gray-500 hover:text-black font-semibold">
            &larr; Back to Website
          </Link>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ auth, onLogout }) => {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [endorsements, setEndorsements] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [issues, setIssues] = useState([]);
  const [galleryDisplayMode, setGalleryDisplayMode] = useState('manual');
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const authConfig = { auth: { username: auth.username, password: auth.password } };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [eventsRes, newsRes, endorsementsRes, subscribersRes, galleryRes, issuesRes, gallerySettingRes] = await Promise.all([
        axios.get(`${API}/admin/events`, authConfig),
        axios.get(`${API}/admin/news`, authConfig),
        axios.get(`${API}/admin/endorsements`, authConfig),
        axios.get(`${API}/admin/subscribers`, authConfig),
        axios.get(`${API}/admin/gallery`, authConfig),
        axios.get(`${API}/admin/issues`, authConfig),
        axios.get(`${API}/admin/settings/gallery_display`, authConfig)
      ]);
      setEvents(eventsRes.data);
      setNews(newsRes.data);
      setEndorsements(endorsementsRes.data);
      setSubscribers(subscribersRes.data);
      setGallery(galleryRes.data);
      setIssues(issuesRes.data);
      setGalleryDisplayMode(gallerySettingRes.data.value || 'manual');
    } catch (e) {
      console.error("Error fetching admin data:", e);
    } finally {
      setLoading(false);
    }
  };

  const updateGalleryDisplayMode = async (mode) => {
    try {
      await axios.put(`${API}/admin/settings/gallery_display`, { value: mode }, authConfig);
      setGalleryDisplayMode(mode);
    } catch (e) {
      console.error("Error updating gallery display mode:", e);
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, authConfig);
      fetchData();
    } catch (e) {
      console.error("Error deleting:", e);
    }
  };

  const handleSave = async (type, data) => {
    try {
      if (editItem) {
        await axios.put(`${API}/admin/${type}/${editItem.id}`, data, authConfig);
      } else {
        await axios.post(`${API}/admin/${type}`, data, authConfig);
      }
      fetchData();
      setShowForm(false);
      setEditItem(null);
    } catch (e) {
      console.error("Error saving:", e);
    }
  };

  const exportSubscribers = () => {
    const csv = [
      ['Email', 'First Name', 'Last Name', 'Phone', 'Wants Email', 'Wants Text', 'Subscribed At'].join(','),
      ...subscribers.map(s => [
        s.email,
        s.first_name || '',
        s.last_name || '',
        s.phone || '',
        s.wants_email ? 'Yes' : 'No',
        s.wants_text ? 'Yes' : 'No',
        s.subscribed_at
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-gray-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-black text-white py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-4">
          <img src={CAMPAIGN_DATA.logoSvg} alt="Logo" className="h-12" />
          <h1 className="font-heading text-xl uppercase">Admin Dashboard</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-gray-300 font-semibold">Welcome, {auth.username}</span>
          <button onClick={onLogout} className="bg-campaign-green px-4 py-2 text-sm font-black hover:bg-campaign-green-hover transition-colors" data-testid="admin-logout">
            Logout
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="p-6">
        {/* Tabs */}
        <div className="flex gap-4 mb-6 flex-wrap">
          <button
            onClick={() => { setActiveTab('events'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'events' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-events-tab"
          >
            Events
          </button>
          <button
            onClick={() => { setActiveTab('news'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'news' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-news-tab"
          >
            News
          </button>
          <button
            onClick={() => { setActiveTab('endorsements'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase ${activeTab === 'endorsements' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-endorsements-tab"
          >
            Endorsements
          </button>
          <button
            onClick={() => { setActiveTab('gallery'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'gallery' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-gallery-tab"
          >
            <Image size={18} /> Gallery ({gallery.length})
          </button>
          <button
            onClick={() => { setActiveTab('issues'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'issues' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-issues-tab"
          >
            <Shield size={18} /> Issues ({issues.length})
          </button>
          <button
            onClick={() => { setActiveTab('subscribers'); setShowForm(false); setEditItem(null); }}
            className={`px-6 py-3 font-semibold uppercase flex items-center gap-2 ${activeTab === 'subscribers' ? 'bg-black text-white' : 'bg-white text-black'}`}
            data-testid="admin-subscribers-tab"
          >
            <Users size={18} /> Subscribers ({subscribers.length})
          </button>
        </div>

        {/* Add Button (not for subscribers) */}
        {activeTab !== 'subscribers' && (
          <div className="mb-6">
            <button
              onClick={() => { setShowForm(true); setEditItem(null); }}
              className="bg-campaign-green text-white px-6 py-3 font-semibold uppercase hover:bg-campaign-green-hover transition-colors"
              data-testid="admin-add-btn"
            >
              Add {activeTab === 'events' ? 'Event' : activeTab === 'news' ? 'News' : activeTab === 'gallery' ? 'Photo' : activeTab === 'issues' ? 'Issue' : 'Endorsement'}
            </button>
          </div>
        )}

        {/* Export Button for subscribers */}
        {activeTab === 'subscribers' && subscribers.length > 0 && (
          <div className="mb-6">
            <button
              onClick={exportSubscribers}
              className="bg-green-600 text-white px-6 py-3 font-semibold uppercase hover:bg-green-700 transition-colors flex items-center gap-2"
              data-testid="admin-export-btn"
            >
              <Download size={18} /> Export CSV
            </button>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="bg-white p-6 rounded-sm shadow mb-6" data-testid="admin-form">
            {activeTab === 'events' ? (
              <EventForm 
                event={editItem} 
                onSave={(data) => handleSave('events', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }} 
              />
            ) : activeTab === 'news' ? (
              <NewsForm 
                article={editItem} 
                onSave={(data) => handleSave('news', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            ) : activeTab === 'gallery' ? (
              <GalleryForm 
                photo={editItem} 
                onSave={(data) => handleSave('gallery', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
                onBulkSave={async (photos) => {
                  for (const photo of photos) {
                    await axios.post(`${API}/admin/gallery`, photo, authConfig);
                  }
                  fetchData();
                }}
              />
            ) : activeTab === 'issues' ? (
              <IssueForm 
                issue={editItem} 
                onSave={(data) => handleSave('issues', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
              />
            ) : (
              <EndorsementForm 
                endorsement={editItem} 
                onSave={(data) => handleSave('endorsements', data)} 
                onCancel={() => { setShowForm(false); setEditItem(null); }}
                authConfig={authConfig}
              />
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-sm shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : activeTab === 'events' ? (
            <table className="w-full" data-testid="admin-events-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="px-6 py-4">{event.title}</td>
                    <td className="px-6 py-4">{event.date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${event.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(event); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('events', event.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr><td colSpan="4" className="px-6 py-8 text-center text-gray-500">No events found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'news' ? (
            <table className="w-full" data-testid="admin-news-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Source</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {news.map((article) => (
                  <tr key={article.id}>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${article.content_type === 'original' ? 'bg-campaign-green/10 text-campaign-green' : 'bg-blue-100 text-blue-800'}`}>
                        {article.content_type === 'original' ? 'Original' : 'External'}
                      </span>
                      {article.video_url && <span className="ml-1 text-xs">🎬</span>}
                    </td>
                    <td className="px-6 py-4 max-w-xs truncate">{article.title}</td>
                    <td className="px-6 py-4">{article.content_type === 'original' ? 'Campaign' : (article.source || '-')}</td>
                    <td className="px-6 py-4">{article.published_date}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${article.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {article.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(article); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('news', article.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {news.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No news articles found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'endorsements' ? (
            <table className="w-full" data-testid="admin-endorsements-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Logo/Image</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {endorsements.map((endorsement) => (
                  <tr key={endorsement.id}>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${endorsement.endorsement_type === 'organization' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                        {endorsement.endorsement_type === 'organization' ? 'Org' : 'Individual'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{endorsement.name}</td>
                    <td className="px-6 py-4">{endorsement.title || '-'}</td>
                    <td className="px-6 py-4">
                      {endorsement.image_url ? (
                        <img src={resolveImageUrl(endorsement.image_url)} alt="" className="w-10 h-10 object-contain rounded" />
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded ${endorsement.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {endorsement.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => { setEditItem(endorsement); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                      <button onClick={() => handleDelete('endorsements', endorsement.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {endorsements.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No endorsements found</td></tr>
                )}
              </tbody>
            </table>
          ) : activeTab === 'gallery' ? (
            <div className="p-6">
              {/* Display Mode Selector */}
              <div className="mb-6 p-4 bg-gray-50 rounded flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-700">Display Order:</span>
                  <select
                    value={galleryDisplayMode}
                    onChange={(e) => updateGalleryDisplayMode(e.target.value)}
                    className="border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                    data-testid="gallery-display-mode"
                  >
                    <option value="manual">Manual (by order number)</option>
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="random">Random (shuffle)</option>
                  </select>
                </div>
                <p className="text-sm text-gray-500">
                  {galleryDisplayMode === 'manual' && 'Photos will display in the order you set (edit each photo to change order number)'}
                  {galleryDisplayMode === 'newest' && 'Most recently added photos will show first'}
                  {galleryDisplayMode === 'oldest' && 'Oldest photos will show first'}
                  {galleryDisplayMode === 'random' && 'Photos will be shuffled randomly on each page load'}
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {gallery.map((photo) => (
                  <div key={photo.id} className="relative group bg-gray-100 rounded overflow-hidden" data-testid={`gallery-admin-${photo.id}`}>
                    <img 
                      src={resolveImageUrl(photo.url)} 
                      alt={photo.caption || 'Gallery photo'}
                      className="w-full aspect-square object-cover"
                    />
                    {galleryDisplayMode === 'manual' && (
                      <div className="absolute top-2 left-2 bg-black text-white text-xs px-2 py-1 rounded font-semibold">
                        #{photo.order || 0}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                      <p className="text-white text-xs text-center line-clamp-2">{photo.caption || 'No caption'}</p>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => { setEditItem(photo); setShowForm(true); }} 
                          className="bg-white text-black px-3 py-1 text-xs font-semibold rounded hover:bg-gray-100"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDelete('gallery', photo.id)} 
                          className="bg-red-600 text-white px-3 py-1 text-xs font-semibold rounded hover:bg-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {gallery.length === 0 && (
                  <div className="col-span-full text-center py-12 text-gray-500">
                    <Image className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                    <p>No photos in gallery. Click "Add Photo" to get started.</p>
                  </div>
                )}
              </div>
            </div>
          ) : activeTab === 'issues' ? (
            <table className="w-full" data-testid="admin-issues-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Order</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Icon</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Description</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {issues.map((issue) => {
                  const IconComponent = ICON_MAP[issue.icon] || Shield;
                  return (
                    <tr key={issue.id}>
                      <td className="px-6 py-4 font-semibold">{issue.order}</td>
                      <td className="px-6 py-4">
                        <div className="w-10 h-10 rounded bg-black/10 flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-black" />
                        </div>
                      </td>
                      <td className="px-6 py-4 font-semibold">{issue.title}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{issue.description}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${issue.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {issue.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => { setEditItem(issue); setShowForm(true); }} className="text-black hover:underline mr-4">Edit</button>
                        <button onClick={() => handleDelete('issues', issue.id)} className="text-red-600 hover:underline">Delete</button>
                      </td>
                    </tr>
                  );
                })}
                {issues.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No issues found. Click "Add Issue" to create platform issues.</td></tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full" data-testid="admin-subscribers-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Preferences</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700 uppercase">Subscribed</th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-gray-700 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {subscribers.map((subscriber) => (
                  <tr key={subscriber.id}>
                    <td className="px-6 py-4">{subscriber.email}</td>
                    <td className="px-6 py-4">{subscriber.first_name || subscriber.last_name ? `${subscriber.first_name || ''} ${subscriber.last_name || ''}`.trim() : '-'}</td>
                    <td className="px-6 py-4">{subscriber.phone || '-'}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        {subscriber.wants_email && <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">Email</span>}
                        {subscriber.wants_text && <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Text</span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(subscriber.subscribed_at).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handleDelete('subscribers', subscriber.id)} className="text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
                {subscribers.length === 0 && (
                  <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No subscribers yet</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

// Event Form
const EventForm = ({ event, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    date: event?.date || 'TBD',
    time: event?.time || '',
    location: event?.location || '',
    rsvp_link: event?.rsvp_link || '#',
    image_url: event?.image_url || '',
    is_active: event?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{event ? 'Edit Event' : 'Add Event'}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Date *</label>
          <input
            type="text"
            value={form.date}
            onChange={(e) => setForm({...form, date: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., TBD, January 15, 2026"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Time</label>
          <input
            type="text"
            value={form.time}
            onChange={(e) => setForm({...form, time: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., 6:00 PM"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Location</label>
          <input
            type="text"
            value={form.location}
            onChange={(e) => setForm({...form, location: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">RSVP Link</label>
          <input
            type="text"
            value={form.rsvp_link}
            onChange={(e) => setForm({...form, rsvp_link: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="Google Form URL or #"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Image URL</label>
          <input
            type="text"
            value={form.image_url}
            onChange={(e) => setForm({...form, image_url: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          />
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="3"
          required
        />
      </div>
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// News Form
const NewsForm = ({ article, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    title: article?.title || '',
    summary: article?.summary || '',
    content: article?.content || '',
    content_type: article?.content_type || 'external',
    source: article?.source || '',
    link: article?.link || '',
    image_url: article?.image_url || '',
    video_url: article?.video_url || '',
    published_date: article?.published_date || new Date().toISOString().split('T')[0],
    is_active: article?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  // Extract YouTube video ID for preview
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  const videoId = getYouTubeId(form.video_url);

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{article ? 'Edit News' : 'Add News'}</h3>
      
      {/* Content Type Selector */}
      <div className="mb-6 p-4 bg-gray-50 rounded">
        <label className="block text-gray-700 font-semibold mb-3">Post Type</label>
        <div className="flex gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="content_type"
              value="external"
              checked={form.content_type === 'external'}
              onChange={(e) => setForm({...form, content_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="font-semibold">External Link</span>
            <span className="text-gray-500 text-sm">(Link to news article)</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="content_type"
              value="original"
              checked={form.content_type === 'original'}
              onChange={(e) => setForm({...form, content_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="font-semibold">Original Post</span>
            <span className="text-gray-500 text-sm">(Your own content/video)</span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Published Date *</label>
          <input
            type="date"
            value={form.published_date}
            onChange={(e) => setForm({...form, published_date: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            required
          />
        </div>
        
        {form.content_type === 'external' ? (
          <>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Source *</label>
              <input
                type="text"
                value={form.source}
                onChange={(e) => setForm({...form, source: e.target.value})}
                className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                placeholder="e.g., Yonkers Tribune"
                required={form.content_type === 'external'}
              />
            </div>
            <div>
              <label className="block text-gray-700 font-semibold mb-2">Article URL *</label>
              <input
                type="url"
                value={form.link}
                onChange={(e) => setForm({...form, link: e.target.value})}
                className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
                placeholder="https://..."
                required={form.content_type === 'external'}
              />
            </div>
          </>
        ) : (
          <div className="md:col-span-2">
            <label className="block text-gray-700 font-semibold mb-2">Video URL (YouTube)</label>
            <input
              type="url"
              value={form.video_url}
              onChange={(e) => setForm({...form, video_url: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="https://www.youtube.com/watch?v=..."
            />
            {videoId && (
              <div className="mt-3">
                <p className="text-sm text-gray-500 mb-2">Preview:</p>
                <div className="aspect-video w-64 bg-black rounded overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${videoId}`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video preview"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Featured Image */}
      <div className="mb-4">
        <ImageUpload 
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          authConfig={authConfig}
          label="Featured Image"
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">
          {form.content_type === 'external' ? 'Summary *' : 'Summary / Excerpt *'}
        </label>
        <textarea
          value={form.summary}
          onChange={(e) => setForm({...form, summary: e.target.value})}
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="3"
          required
          placeholder={form.content_type === 'external' ? 'Brief summary of the article...' : 'Short excerpt shown in the news list...'}
        />
      </div>
      
      {/* Full Content for Original Posts */}
      {form.content_type === 'original' && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Full Content</label>
          <RichTextEditor
            value={form.content}
            onChange={(content) => setForm({...form, content})}
          />
          <p className="text-sm text-gray-500 mt-1">Use the toolbar above to format your content</p>
        </div>
      )}
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Image Upload Component
const ImageUpload = ({ value, onChange, authConfig, label = "Image" }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(resolveImageUrl(value) || '');
  const fileInputRef = React.useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload file
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/admin/upload`, formData, {
        ...authConfig,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Store just the relative path - resolveImageUrl will add the correct domain
      const uploadedUrl = res.data.url;
      onChange(uploadedUrl);
      setPreview(resolveImageUrl(uploadedUrl));
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Failed to upload image. Please try again.');
      setPreview(value || '');
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    onChange(url);
    setPreview(url);
  };

  const handleRemove = () => {
    onChange('');
    setPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <label className="block text-gray-700 font-semibold mb-2">{label}</label>
      
      {/* Preview */}
      {preview && (
        <div className="mb-3 relative inline-block">
          <img 
            src={preview} 
            alt="Preview" 
            className="w-24 h-24 object-contain border rounded bg-gray-50"
          />
          <button 
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
          >
            ×
          </button>
        </div>
      )}
      
      <div className="flex gap-2 items-center">
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id={`file-upload-${label.replace(/\s/g, '-')}`}
        />
        <label 
          htmlFor={`file-upload-${label.replace(/\s/g, '-')}`}
          className={`cursor-pointer bg-gray-100 border border-gray-300 px-4 py-2 rounded text-sm font-semibold hover:bg-gray-200 transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        >
          {uploading ? 'Uploading...' : 'Upload Image'}
        </label>
        <span className="text-gray-400 text-sm">or</span>
        <input
          type="text"
          placeholder="Paste image URL"
          value={preview.startsWith('data:') ? '' : (preview || '')}
          onChange={handleUrlChange}
          className="flex-1 border border-gray-300 px-3 py-2 rounded text-sm focus:outline-none focus:border-black"
        />
      </div>
    </div>
  );
};

// Gallery Form (with bulk upload support for new photos)
const GalleryForm = ({ photo, onSave, onCancel, authConfig, onBulkSave }) => {
  const [form, setForm] = useState({
    url: photo?.url || '',
    caption: photo?.caption || '',
    order: photo?.order || 0,
    is_active: photo?.is_active ?? true
  });
  
  // Bulk upload state
  const [bulkMode, setBulkMode] = useState(!photo); // Default to bulk mode for new photos
  const [bulkFiles, setBulkFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const dropZoneRef = React.useRef(null);
  const bulkInputRef = React.useRef(null);
  
  // Cropping state
  const [cropperImage, setCropperImage] = useState(null);
  const [cropperIndex, setCropperIndex] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.url) {
      alert('Please upload or enter an image URL');
      return;
    }
    onSave(form);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 30) {
      alert('Maximum 30 photos at a time');
      return;
    }
    setBulkFiles(prev => [...prev, ...files].slice(0, 30));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleBulkFileSelect = (e) => {
    const files = Array.from(e.target.files).filter(f => f.type.startsWith('image/'));
    if (files.length > 30) {
      alert('Maximum 30 photos at a time');
      return;
    }
    setBulkFiles(prev => [...prev, ...files].slice(0, 30));
  };

  const removeBulkFile = (index) => {
    setBulkFiles(prev => prev.filter((_, i) => i !== index));
  };
  
  // Open cropper for a specific image
  const openCropper = (index) => {
    const file = bulkFiles[index];
    const reader = new FileReader();
    reader.onload = () => {
      setCropperImage(reader.result);
      setCropperIndex(index);
    };
    reader.readAsDataURL(file);
  };
  
  // Handle crop complete
  const handleCropComplete = (croppedBlob) => {
    // Create a new file from the cropped blob
    const croppedFile = new File([croppedBlob], bulkFiles[cropperIndex].name, { type: 'image/jpeg' });
    
    // Replace the file in bulkFiles
    setBulkFiles(prev => {
      const newFiles = [...prev];
      newFiles[cropperIndex] = croppedFile;
      return newFiles;
    });
    
    // Close cropper
    setCropperImage(null);
    setCropperIndex(null);
  };

  const handleBulkUpload = async () => {
    if (bulkFiles.length === 0) return;
    
    setUploading(true);
    setUploadProgress({ current: 0, total: bulkFiles.length });
    
    const uploadedPhotos = [];
    
    for (let i = 0; i < bulkFiles.length; i++) {
      const file = bulkFiles[i];
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/admin/upload`, formData, {
          ...authConfig,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        // Store just the relative path
        const uploadedUrl = res.data.url;
        uploadedPhotos.push({
          url: uploadedUrl,
          caption: '',
          order: i,
          is_active: true
        });
        
        setUploadProgress({ current: i + 1, total: bulkFiles.length });
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }
    
    setUploading(false);
    
    if (uploadedPhotos.length > 0 && onBulkSave) {
      await onBulkSave(uploadedPhotos);
    }
    
    setBulkFiles([]);
    onCancel();
  };

  // If editing existing photo, show single edit form
  if (photo) {
    return (
      <form onSubmit={handleSubmit}>
        <h3 className="font-heading text-xl text-black uppercase mb-4">Edit Photo</h3>
        
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Current Photo</label>
          <img 
            src={resolveImageUrl(form.url)} 
            alt="Current" 
            className="w-32 h-32 object-cover rounded border"
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Caption</label>
            <input
              type="text"
              value={form.caption}
              onChange={(e) => setForm({...form, caption: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="Describe this photo..."
            />
          </div>
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
            <input
              type="number"
              value={form.order}
              onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              min="0"
            />
          </div>
        </div>
        
        <div className="mb-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({...form, is_active: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="text-gray-700 font-semibold">Active (visible on website)</span>
          </label>
        </div>
        
        <div className="flex gap-4">
          <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
            Save
          </button>
          <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
            Cancel
          </button>
        </div>
      </form>
    );
  }

  // Bulk upload form for new photos
  return (
    <div>
      <h3 className="font-heading text-xl text-black uppercase mb-4">Add Photos (up to 30)</h3>
      
      {/* Drop zone */}
      <div
        ref={dropZoneRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-black transition-colors cursor-pointer mb-4"
        onClick={() => bulkInputRef.current?.click()}
      >
        <input
          type="file"
          ref={bulkInputRef}
          accept="image/*"
          multiple
          onChange={handleBulkFileSelect}
          className="hidden"
        />
        <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-semibold mb-1">Drop photos here or click to select</p>
        <p className="text-gray-400 text-sm">Upload up to 30 photos at once. Add captions later.</p>
      </div>
      
      {/* Preview of selected files */}
      {bulkFiles.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-gray-700 font-semibold">{bulkFiles.length} photo(s) selected</p>
            <p className="text-sm text-gray-500">Click <Crop size={14} className="inline" /> to crop any photo</p>
          </div>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2 max-h-64 overflow-y-auto p-1">
            {bulkFiles.map((file, index) => (
              <div key={index} className="relative group">
                <img 
                  src={URL.createObjectURL(file)} 
                  alt={`Preview ${index + 1}`}
                  className="w-full aspect-square object-cover rounded border-2 border-transparent group-hover:border-black transition-colors"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => openCropper(index)}
                    className="bg-white text-black p-1.5 rounded hover:bg-gray-100"
                    title="Crop image"
                  >
                    <Crop size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeBulkFile(index)}
                    className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600"
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Image Cropper Modal */}
      {cropperImage && (
        <ImageCropperModal
          imageSrc={cropperImage}
          onCropComplete={handleCropComplete}
          onCancel={() => { setCropperImage(null); setCropperIndex(null); }}
          aspectRatio={1}
        />
      )}
      
      {/* Upload progress */}
      {uploading && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="animate-spin w-4 h-4 border-2 border-black border-t-transparent rounded-full"></div>
            <span className="text-gray-600">Uploading {uploadProgress.current} of {uploadProgress.total}...</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-black h-2 rounded-full transition-all duration-300"
              style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      <div className="flex gap-4">
        <button 
          type="button"
          onClick={handleBulkUpload}
          disabled={bulkFiles.length === 0 || uploading}
          className="bg-campaign-green text-white px-6 py-2 font-semibold hover:bg-campaign-green-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {uploading ? 'Uploading...' : `Upload ${bulkFiles.length} Photo${bulkFiles.length !== 1 ? 's' : ''}`}
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  );
};

// Endorsement Form
const EndorsementForm = ({ endorsement, onSave, onCancel, authConfig }) => {
  const [form, setForm] = useState({
    name: endorsement?.name || '',
    title: endorsement?.title || '',
    organization: endorsement?.organization || '',
    quote: endorsement?.quote || '',
    image_url: endorsement?.image_url || '',
    endorsement_type: endorsement?.endorsement_type || 'individual',
    is_active: endorsement?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{endorsement ? 'Edit Endorsement' : 'Add Endorsement'}</h3>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Endorsement Type *</label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endorsement_type"
              value="organization"
              checked={form.endorsement_type === 'organization'}
              onChange={(e) => setForm({...form, endorsement_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="flex items-center gap-1"><Building2 size={16} /> Organization</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="endorsement_type"
              value="individual"
              checked={form.endorsement_type === 'individual'}
              onChange={(e) => setForm({...form, endorsement_type: e.target.value})}
              className="w-4 h-4"
            />
            <span className="flex items-center gap-1"><User size={16} /> Individual</span>
          </label>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            {form.endorsement_type === 'organization' ? 'Organization Name *' : 'Name *'}
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder={form.endorsement_type === 'organization' ? 'e.g., Yonkers Republican Committee' : 'e.g., Mike Spano'}
            required
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">
            {form.endorsement_type === 'organization' ? 'Tagline/Description' : 'Title'}
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder={form.endorsement_type === 'organization' ? 'e.g., Serving Yonkers since 1920' : 'e.g., Mayor of Yonkers'}
          />
        </div>
        {form.endorsement_type === 'individual' && (
          <div>
            <label className="block text-gray-700 font-semibold mb-2">Organization</label>
            <input
              type="text"
              value={form.organization}
              onChange={(e) => setForm({...form, organization: e.target.value})}
              className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder="e.g., Yonkers City Council"
            />
          </div>
        )}
      </div>
      
      {/* Image Upload */}
      <div className="mb-4">
        <ImageUpload 
          value={form.image_url}
          onChange={(url) => setForm({...form, image_url: url})}
          authConfig={authConfig}
          label={form.endorsement_type === 'organization' ? 'Organization Logo' : 'Photo'}
        />
      </div>
      
      {form.endorsement_type === 'individual' && (
        <div className="mb-4">
          <label className="block text-gray-700 font-semibold mb-2">Quote</label>
          <textarea
            value={form.quote}
            onChange={(e) => setForm({...form, quote: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            rows="3"
            placeholder="Optional endorsement quote..."
          />
        </div>
      )}
      
      <div className="mb-6">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={form.is_active}
            onChange={(e) => setForm({...form, is_active: e.target.checked})}
            className="w-5 h-5"
          />
          <span className="text-gray-700 font-semibold">Active (visible on website)</span>
        </label>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Issue Form
const IssueForm = ({ issue, onSave, onCancel }) => {
  const [form, setForm] = useState({
    title: issue?.title || '',
    icon: issue?.icon || 'Shield',
    description: issue?.description || '',
    details: issue?.details || [''],
    order: issue?.order || 0,
    is_active: issue?.is_active ?? true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Filter out empty details
    const cleanedDetails = form.details.filter(d => d.trim() !== '');
    onSave({ ...form, details: cleanedDetails });
  };

  const addDetail = () => {
    setForm({ ...form, details: [...form.details, ''] });
  };

  const removeDetail = (index) => {
    setForm({ ...form, details: form.details.filter((_, i) => i !== index) });
  };

  const updateDetail = (index, value) => {
    const newDetails = [...form.details];
    newDetails[index] = value;
    setForm({ ...form, details: newDetails });
  };

  const iconOptions = Object.keys(ICON_MAP);

  return (
    <form onSubmit={handleSubmit}>
      <h3 className="font-heading text-xl text-black uppercase mb-4">{issue ? 'Edit Issue' : 'Add Issue'}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="md:col-span-2">
          <label className="block text-gray-700 font-semibold mb-2">Issue Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({...form, title: e.target.value})}
            required
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            placeholder="e.g., Fix Public Safety"
          />
        </div>
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Icon</label>
          <select
            value={form.icon}
            onChange={(e) => setForm({...form, icon: e.target.value})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          >
            {iconOptions.map(icon => (
              <option key={icon} value={icon}>{icon}</option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Description *</label>
        <textarea
          value={form.description}
          onChange={(e) => setForm({...form, description: e.target.value})}
          required
          className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
          rows="2"
          placeholder="Brief description shown on the card..."
        />
      </div>
      
      <div className="mb-4">
        <label className="block text-gray-700 font-semibold mb-2">Key Points</label>
        {form.details.map((detail, index) => (
          <div key={index} className="flex gap-2 mb-2">
            <input
              type="text"
              value={detail}
              onChange={(e) => updateDetail(index, e.target.value)}
              className="flex-1 border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
              placeholder={`Point ${index + 1}...`}
            />
            <button
              type="button"
              onClick={() => removeDetail(index)}
              className="px-3 py-2 bg-red-100 text-red-600 rounded hover:bg-red-200"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addDetail}
          className="text-black text-sm font-semibold hover:underline"
        >
          + Add another point
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-gray-700 font-semibold mb-2">Display Order</label>
          <input
            type="number"
            value={form.order}
            onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
            className="w-full border border-gray-300 px-4 py-2 rounded focus:outline-none focus:border-black"
            min="0"
          />
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({...form, is_active: e.target.checked})}
              className="w-5 h-5"
            />
            <span className="text-gray-700 font-semibold">Active (visible on website)</span>
          </label>
        </div>
      </div>
      
      <div className="flex gap-4">
        <button type="submit" className="bg-black text-white px-6 py-2 font-semibold hover:bg-black-light transition-colors">
          Save
        </button>
        <button type="button" onClick={onCancel} className="bg-gray-300 text-gray-700 px-6 py-2 font-semibold hover:bg-gray-400 transition-colors">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Admin Page Wrapper
const AdminPage = () => {
  const [auth, setAuth] = useState(null);

  const handleLogin = (credentials) => {
    setAuth(credentials);
    localStorage.setItem('adminAuth', JSON.stringify(credentials));
  };

  const handleLogout = () => {
    setAuth(null);
    localStorage.removeItem('adminAuth');
  };

  useEffect(() => {
    const savedAuth = localStorage.getItem('adminAuth');
    if (savedAuth) {
      setAuth(JSON.parse(savedAuth));
    }
  }, []);

  if (!auth) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard auth={auth} onLogout={handleLogout} />;
};

// Main App Layout
const MainLayout = ({ children }) => {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainLayout><Home /></MainLayout>} />
          <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
          <Route path="/events" element={<MainLayout><EventsPage /></MainLayout>} />
          <Route path="/events/:eventId" element={<MainLayout><EventDetailPage /></MainLayout>} />
          <Route path="/news" element={<MainLayout><NewsPage /></MainLayout>} />
          <Route path="/terms-and-conditions" element={<MainLayout><TermsPage /></MainLayout>} />
          <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
